import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { API_URL } from '../api';

// A file of the media store, as an article embeds it: /media/<xx>/<name>.
const MEDIA_FILE = /\/media\/[0-9a-f]{2}\/([^/?#]+)$/i;

function languageName(code, locale) {
  try {
    return new Intl.DisplayNames([locale], { type: 'language' }).of(code) ?? code;
  } catch {
    return code;
  }
}

/**
 * The subtitle tracks stored for a media file, as object URLs a <track> can
 * load. Fetched rather than pointed at the API directly: a <track> on another
 * origin (the API in development) needs CORS on the video as well, and the
 * session cookie goes along with fetch either way.
 */
function useSubtitleTracks(src) {
  const [tracks, setTracks] = useState([]);

  useEffect(() => {
    const fileName = src?.match(MEDIA_FILE)?.[1];
    if (!fileName) return undefined;

    let cancelled = false;
    const urls = [];

    (async () => {
      try {
        const response = await fetch(
          `${API_URL}/subtitles/file/${encodeURIComponent(fileName)}`,
          { credentials: 'include' },
        );
        if (!response.ok) return;

        const loaded = [];
        for (const track of await response.json()) {
          const vtt = await fetch(`${API_URL}/subtitles/track/${track.track_id}/vtt`, {
            credentials: 'include',
          });
          if (!vtt.ok) continue;

          const url = URL.createObjectURL(new Blob([await vtt.text()], { type: 'text/vtt' }));
          urls.push(url);
          loaded.push({ id: track.track_id, language: track.language, url });
        }

        if (!cancelled) setTracks(loaded);
      } catch (error) {
        // a video without subtitles still plays
        console.error('Could not load subtitles:', error);
      }
    })();

    return () => {
      cancelled = true;
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [src]);

  return tracks;
}

/** A video in an article, with its stored subtitles and a button for them. */
function ArticleVideo({ src, openButton }) {
  const { t, i18n } = useTranslation();
  const videoRef = useRef(null);
  const tracks = useSubtitleTracks(src);
  const [showing, setShowing] = useState(false);

  // The reader's language first, else the first track there is.
  const preferred = Math.max(
    0,
    tracks.findIndex((track) => i18n.language.startsWith(track.language)),
  );

  const showSubtitles = useCallback((on) => {
    const textTracks = videoRef.current?.textTracks;
    if (!textTracks) return;

    for (let i = 0; i < textTracks.length; i += 1) {
      textTracks[i].mode = on && i === preferred ? 'showing' : 'hidden';
    }
    setShowing(on);
  }, [preferred]);

  // Subtitles a video has are on from the start; the button turns them off.
  // Set once the <track> elements are in, as they arrive after the video.
  useEffect(() => {
    if (tracks.length > 0) showSubtitles(true);
  }, [tracks, showSubtitles]);

  // The player's own captions menu switches them too; the button follows.
  useEffect(() => {
    const textTracks = videoRef.current?.textTracks;
    if (!textTracks) return undefined;

    const sync = () => {
      setShowing(Array.from(textTracks).some((track) => track.mode === 'showing'));
    };
    textTracks.addEventListener('change', sync);
    return () => textTracks.removeEventListener('change', sync);
  }, []);

  function toggleSubtitles() {
    if (tracks.length === 0) {
      alert(t('articlePage.subtitlesUnavailable'));
      return;
    }

    showSubtitles(!showing);
  }

  return (
    <span className="article-video-wrapper">
      <video
        ref={videoRef}
        src={src}
        controls
        controlsList="nofullscreen"
        preload="metadata"
        className="article-video"
      >
        {tracks.map((track, index) => (
          <track
            key={track.id}
            kind="subtitles"
            src={track.url}
            srcLang={track.language}
            label={languageName(track.language, i18n.language)}
            default={index === preferred}
          />
        ))}
      </video>

      {/* Over the picture, not the player's control bar: the bar's own
          buttons move whenever the browser adds one (captions, for one). */}
      <span className="article-video-actions">
        <button
          type="button"
          className={`article-subtitles-button${showing ? ' active' : ''}`}
          onClick={toggleSubtitles}
          aria-label={t('articlePage.subtitlesButton')}
          aria-pressed={tracks.length > 0 ? showing : undefined}
        >
          <i className="fa-solid fa-closed-captioning" />
          <span className="article-subtitles-tooltip">
            {t('articlePage.subtitlesButton')}
          </span>
        </button>

        {openButton}
      </span>
    </span>
  );
}

export default ArticleVideo;
